(function () {
  if (window.__bf2026TabbedProducts) return;
  window.__bf2026TabbedProducts = true;

  const tabSelector = '.tabbed-collections--bf2026';
  const productSectionSelector = '.section-featured-collection';

  function setStatus(section, message, isError) {
    const status = section.querySelector('[data-bf2026-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('bf2026-tabs-status--error', Boolean(isError));
  }

  function setActive(tabs, activeTab) {
    tabs.forEach((tab) => {
      const active = tab === activeTab;
      tab.classList.toggle('is-active', active);
      if (active) tab.setAttribute('aria-current', 'true');
      else tab.removeAttribute('aria-current');
    });
  }

  function getProductSection() {
    return document.querySelector(productSectionSelector);
  }

  function getCollectionUrl(href, page) {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin || !/(^|\/)collections\/[^/]+/.test(url.pathname)) {
      throw new Error('Invalid collection URL');
    }
    url.searchParams.set('page', String(page));
    return url;
  }

  async function fetchProductGrid(href, page, signal) {
    const response = await fetch(getCollectionUrl(href, page), {
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.querySelector('#product-grid');
    if (!grid) throw new Error('Collection product grid missing');
    return grid;
  }

  function scrollToGrid(grid) {
    grid.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  function initSection(section) {
    if (section.dataset.bf2026Initialized === 'true') return;

    const tabs = Array.from(section.querySelectorAll('.tabbed-collections__tab'));
    if (!tabs.length) return;
    const productSection = getProductSection();
    const grid = productSection?.querySelector('.product-grid');
    if (!grid) return;
    section.dataset.bf2026Initialized = 'true';
    productSection.classList.add('bf2026-products');
    // The installed Swatch King app scans cards inside this marker on custom pages.
    productSection.setAttribute('contains-product-blocks', '');
    grid.id = `bf2026-product-grid-${section.id || 'main'}`;
    grid.tabIndex = -1;
    tabs.forEach((tab) => tab.setAttribute('aria-controls', grid.id));
    setActive(tabs, tabs[0]);

    const listing = grid.closest('.collection-listing');
    const gridMessage = document.createElement('p');
    gridMessage.className = 'bf2026-grid-message';
    gridMessage.setAttribute('role', 'status');
    gridMessage.hidden = true;
    listing?.insertBefore(gridMessage, grid);
    const loadMore = document.createElement('button');
    loadMore.type = 'button';
    loadMore.className = 'bf2026-load-more';
    loadMore.textContent = 'Load more';
    loadMore.hidden = true;
    listing?.appendChild(loadMore);
    const autoLoad = document.createElement('div');
    autoLoad.className = 'bf2026-auto-load';
    autoLoad.textContent = 'Loading more products…';
    autoLoad.setAttribute('role', 'status');
    autoLoad.hidden = true;
    listing?.appendChild(autoLoad);

    let activeRequest = null;
    let loadMoreRequest = null;
    const listenersController = new AbortController();
    let currentHref = tabs[0].href;
    let currentPage = 1;
    let totalPages = 1;
    let autoLoadFailed = false;
    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) loadNextPage();
        }, { rootMargin: '400px 0px', threshold: 0.01 })
      : null;

    function updateLoadState() {
      const hasMore = currentPage < totalPages;
      observer?.unobserve(autoLoad);
      autoLoad.hidden = !hasMore || !observer || autoLoadFailed;
      loadMore.hidden = !hasMore || (Boolean(observer) && !autoLoadFailed);
      if (hasMore && observer && !autoLoadFailed) observer.observe(autoLoad);
    }

    async function loadNextPage() {
      if (activeRequest || loadMoreRequest || currentPage >= totalPages) return;
      const controller = new AbortController();
      loadMoreRequest = controller;
      loadMore.disabled = true;
      loadMore.textContent = 'Loading…';
      gridMessage.hidden = true;
      try {
        const sourceGrid = await fetchProductGrid(currentHref, currentPage + 1, controller.signal);
        if (loadMoreRequest !== controller) return;
        const products = Array.from(sourceGrid.children).filter((item) => item.classList.contains('product-block'));
        grid.append(...products);
        currentPage = Number(sourceGrid.dataset.currentPage) || currentPage + 1;
        totalPages = products.length ? Number(sourceGrid.dataset.totalPages) || totalPages : currentPage;
        autoLoadFailed = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          autoLoadFailed = true;
          gridMessage.textContent = 'Could not load more products. Please try again.';
          gridMessage.hidden = false;
        }
      } finally {
        if (loadMoreRequest === controller) {
          loadMoreRequest = null;
          loadMore.disabled = false;
          loadMore.textContent = 'Load more';
          updateLoadState();
        }
      }
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', async (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (!getProductSection()?.contains(grid)) return;
        event.preventDefault();
        const keyboardActivation = event.detail === 0;

        if (tab.href === currentHref && tab.classList.contains('is-active')) {
          scrollToGrid(grid);
          if (keyboardActivation) grid.focus({ preventScroll: true });
          return;
        }

        activeRequest?.abort();
        loadMoreRequest?.abort();
        loadMoreRequest = null;
        observer?.unobserve(autoLoad);
        autoLoad.hidden = true;
        loadMore.hidden = true;
        loadMore.disabled = false;
        loadMore.textContent = 'Load more';
        const controller = new AbortController();
        activeRequest = controller;
        section.setAttribute('aria-busy', 'true');
        grid.setAttribute('aria-busy', 'true');
        setStatus(section, `Loading ${tab.textContent.trim()} products…`, false);
        gridMessage.hidden = true;
        gridMessage.replaceChildren();
        scrollToGrid(grid);

        try {
          const sourceGrid = await fetchProductGrid(tab.href, 1, controller.signal);
          if (activeRequest !== controller) return;

          const oldLoader = productSection.querySelector('.load-more-container[data-section-id]');
          oldLoader?.__featuredCollectionLoadMoreCleanup?.();
          oldLoader?.remove();
          productSection.querySelector('.pagination')?.remove();

          const products = Array.from(sourceGrid.children).filter((item) => item.classList.contains('product-block'));
          grid.className = sourceGrid.className;
          grid.replaceChildren(...products);
          const headingLink = productSection.querySelector('.has-paging__title');
          if (headingLink) {
            headingLink.href = tab.href;
            headingLink.textContent = tab.textContent.trim();
          }
          currentHref = tab.href;
          currentPage = Number(sourceGrid.dataset.currentPage) || 1;
          totalPages = Number(sourceGrid.dataset.totalPages) || 1;
          autoLoadFailed = false;
          setActive(tabs, tab);
          setStatus(section, products.length ? '' : 'No products are available in this collection.', false);
          scrollToGrid(grid);
          if (keyboardActivation) grid.focus({ preventScroll: true });
        } catch (error) {
          if (error.name !== 'AbortError') {
            setStatus(section, `Could not load ${tab.textContent.trim()} products.`, true);
            gridMessage.textContent = `Could not load ${tab.textContent.trim()} products. `;
            const link = document.createElement('a');
            link.href = tab.href;
            link.textContent = 'Open the collection';
            gridMessage.appendChild(link);
            gridMessage.hidden = false;
          }
        } finally {
          if (activeRequest === controller) {
            activeRequest = null;
            section.removeAttribute('aria-busy');
            grid.removeAttribute('aria-busy');
            updateLoadState();
          }
        }
      }, { signal: listenersController.signal });
    });

    loadMore.addEventListener('click', loadNextPage, { signal: listenersController.signal });
    section.__bf2026Cleanup = () => {
      activeRequest?.abort();
      loadMoreRequest?.abort();
      observer?.disconnect();
      listenersController.abort();
      gridMessage.remove();
      loadMore.remove();
      autoLoad.remove();
      section.dataset.bf2026Initialized = 'false';
    };
  }

  function init(root) {
    if (root.matches?.(tabSelector)) initSection(root);
    root.querySelectorAll?.(tabSelector).forEach(initSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => {
    const changed = event.target;
    if (changed.matches?.(tabSelector) || changed.querySelector?.(tabSelector) ||
        changed.matches?.(productSectionSelector) || changed.querySelector?.(productSectionSelector)) {
      document.querySelectorAll(tabSelector).forEach((section) => section.__bf2026Cleanup?.());
      init(document);
    }
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(tabSelector)) event.target.__bf2026Cleanup?.();
    event.target.querySelectorAll?.(tabSelector).forEach((section) => section.__bf2026Cleanup?.());
  });
})();
